import { Backdrop, Box, Button, CircularProgress, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useAppContext } from '../../contexts/AppContext';
import { useFormContext } from '../../contexts/FormContext';
import { useFormData } from '../../hooks/useFormValues';
import ChartHelpTooltip from '../common/ChartHelpTooltip';
import FieldRenderer from './FieldRenderer';
import { useFormRendererContext } from './FormRendererContext';
import type { FieldSchema } from './types';

const SoilingSensorDataSection = ({ field }: { field: FieldSchema }) => {
  const { setModified } = useAppContext();
  const { setFormData, getFormDataItem, updateDataValue, clearSoilingRatioSelections } = useFormContext();
  const formData = useFormData();
  const { isLoadingSensorData } = useFormRendererContext();

  const soilingDataKeys = Object.keys(formData).filter((key) => key === 'soiling_sensor_data' || key.startsWith('soiling_sensor_data_'));

  return (
    <Grid size={12}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box display="flex" alignItems="center">
          <Typography color="text.primary" variant={field.font_size || 'h6'}>
            {field.label}
          </Typography>
          {Object.prototype.hasOwnProperty.call(field, 'help') ? <ChartHelpTooltip chartType={field.help as string} /> : null}
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<ContentCopyIcon />}
          disabled={isLoadingSensorData}
          onClick={() => {
            const existingBlocks = Object.keys(formData).filter(
              (key) => key === 'soiling_sensor_data' || key.startsWith('soiling_sensor_data_'),
            );
            const nextOrder = existingBlocks.length + 1;
            const newBlockName = `soiling_sensor_data_${nextOrder}`;

            const newBlockData = {
              file_uid: null,
              name: `Sensor ${nextOrder}`,
              _order: nextOrder,
              type: 'rde300',
            };

            const newFormData = JSON.parse(JSON.stringify(formData));
            updateDataValue(newFormData, newBlockName, newBlockData);
            setFormData(newFormData);
          }}
        >
          Add New Block
        </Button>
      </Box>

      {isLoadingSensorData ? (
        <Backdrop open={true} sx={{ width: '100%', height: '450px', position: 'relative' }}>
          <CircularProgress />
        </Backdrop>
      ) : soilingDataKeys.length > 0 ? (
        soilingDataKeys
          .sort((a, b) => {
            const orderA = getFormDataItem<number>(formData, `${a}._order`) || 0;
            const orderB = getFormDataItem<number>(formData, `${b}._order`) || 0;
            return orderB - orderA;
          })
          .map((blockKey) => {
            return (
              <Grid
                size={12}
                key={blockKey}
                sx={{
                  padding: 1,
                  border: '1px solid rgba(0, 0, 0, 0.12)',
                  borderRadius: '4px',
                  boxShadow: '2px 2px 4px rgba(0, 0, 0, 0.2)',
                  mb: 2,
                }}
              >
                <Box display="flex" justifyContent="flex-end" alignItems="center" sx={{ mb: 1 }}>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => {
                      const fileUidToRemove = getFormDataItem<string | undefined>(formData, `${blockKey}.file_uid`);

                      const newFormData = { ...formData };
                      delete newFormData[blockKey];
                      setFormData(newFormData);

                      if (fileUidToRemove) {
                        clearSoilingRatioSelections('soiling_ratio_operational', fileUidToRemove);
                        clearSoilingRatioSelections('soiling_ratio_operational_ex_post', fileUidToRemove);
                      }

                      setModified(true);
                    }}
                  >
                    Remove
                  </Button>
                </Box>
                <Grid container spacing={2}>
                  {field.fields?.map((subField) => {
                    const upload = subField.upload as (FieldSchema & { target?: string }) | undefined;
                    const clonedField: FieldSchema = {
                      ...subField,
                      name: subField.name!.replace('soiling_sensor_data', blockKey),
                      upload: upload
                        ? {
                            ...upload,
                            name: upload.name ? upload.name.replace('soiling_sensor_data', blockKey) : upload.name,
                            target: upload.target ? upload.target.replace('soiling_sensor_data', blockKey) : upload.target,
                          }
                        : upload,
                    };
                    if (clonedField.name!.endsWith('.name')) {
                      clonedField.required = true;
                    }
                    return <FieldRenderer key={clonedField.name} field={clonedField} />;
                  })}
                </Grid>
              </Grid>
            );
          })
      ) : (
        <Grid
          size={12}
          sx={{
            padding: 2,
            border: '1px dashed rgba(0, 0, 0, 0.12)',
            borderRadius: '4px',
            textAlign: 'center',
            color: 'text.secondary',
          }}
        >
          <Typography variant="body2">No soiling sensor data blocks yet. Click "Add New Block" to create one.</Typography>
        </Grid>
      )}
    </Grid>
  );
};

export default SoilingSensorDataSection;
